import type { AuthClient } from "@nav-games/auth-client";
import { setPremium } from "@nav-games/games-core";

import { WORKER_BASE_URL } from "./config";

interface EntitlementsResponse {
  premium: boolean;
  expiresAt: number | null | undefined;
}

/// Fetch `/me/entitlements` from nav-games-worker and propagate the
/// premium flag into `games-core` (which the rest of the UI reads
/// via `isPremium()`). Silently no-ops on network failure or
/// missing session — the UI falls back to the cached localStorage
/// value, so a brief offline period doesn't lock the user out of
/// content they've already paid for.
///
/// Called from `App.tsx` once after sign-in, and again after the
/// IAP purchase flow lands a new transaction. No polling loop —
/// renewals + revocations are pushed via the worker's App Store /
/// Play webhooks, and the SPA re-reads on next app open.
export async function refreshEntitlements(auth: AuthClient): Promise<void> {
  const session = auth.session();
  // Belt-and-braces: bail when session is missing OR has an empty
  // accessToken. The empty-token case happens with a stale half-saved
  // localStorage entry (rare but real); without this guard we'd send
  // `Authorization: Bearer ` and the worker would log a 401 +
  // "missing_session" that pollutes DevTools.
  if (!session || !session.accessToken) return;
  try {
    const resp = await fetch(`${WORKER_BASE_URL}/me/entitlements`, {
      headers: { Authorization: `Bearer ${session.accessToken}` },
    });
    if (!resp.ok) {
      // 401 means the access token expired; the auth client's
      // refresh flow will kick in on the next request. Don't
      // clear premium on a transient auth error.
      return;
    }
    const body = (await resp.json()) as EntitlementsResponse;
    setPremium(body.premium);
  } catch {
    /* network error — leave the cached premium flag alone */
  }
}
